import { I18n } from '@affine/i18n';
import { SignalWatcher, WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVarV2 } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { DEFAULT_IMAGE_PROXY_ENDPOINT } from '@blocksuite/affine-shared/consts';
import {
  CloseIcon,
  DoneIcon,
  ToggleDownIcon,
  ToolIcon,
} from '@blocksuite/icons/lit';
import { type Signal } from '@preact/signals-core';
import { css, html, nothing, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';

import {
  aiChatControlTypography,
  aiChatMetaTypography,
  aiChatStatusTypography,
} from '../../styles/ai-chat-typography';

export interface ToolResult {
  title: string | TemplateResult<1>;
  icon?: string | TemplateResult<1>;
  content?: string;
  href?: string;
  onClick?: () => void;
  actionLabel?: string;
}

export class ToolResultCard extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  static override styles = css`
    .ai-tool-result-wrapper {
      display: block;
      padding: 6px 10px;
      margin: 4px 0;
      border-radius: 8px;
      border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
      background-color: ${unsafeCSSVarV2('layer/background/primary')};
      color: ${unsafeCSSVarV2('icon/secondary')};
      transition: color 0.23s ease;

      .ai-tool-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 6px;
        margin-right: 3px;
        cursor: pointer;
      }

      .ai-icon {
        width: 18px;
        height: 18px;
        flex-shrink: 0;

        svg {
          width: 18px;
          height: 18px;
          display: block;
          flex-shrink: 0;
        }
      }

      .ai-icon.success svg {
        color: #34a853;
      }

      .ai-icon.error svg {
        color: #d93025;
      }

      .ai-tool-name {
        ${aiChatStatusTypography}
        font-weight: 500;
        margin-left: 0px;
        margin-right: auto;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .ai-tool-name.success {
        color: #34a853;
      }

      .ai-tool-name.error {
        color: #d93025;
      }

      .ai-tool-results {
        display: grid;
        grid-template-rows: 1fr;
        transition:
          grid-template-rows 0.4s cubic-bezier(0.07, 0.83, 0.46, 1),
          opacity 0.4s ease,
          margin-top 0.23s ease,
          transform 0.43s ease;
        padding-left: 11px;
        margin-top: 4px;
        transform-origin: bottom;
      }

      .ai-tool-results[data-collapsed='true'] {
        grid-template-rows: 0fr;
        opacity: 0;
        transform: translateY(10px);
        margin-top: 0px;
      }

      .ai-tool-result-collapse-wrapper {
        overflow: hidden;
      }

      .ai-tool-results-content {
        display: flex;
        flex-direction: column;
        padding: 4px 2px 4px 20px;
        border-left: 1px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
      }

      .result-item {
        margin-top: 16px;
        display: block;
        width: 100%;
        min-height: 44px;
        padding: 4px 6px;
        border: 0;
        border-radius: 10px;
        background: transparent;
        color: inherit;
        cursor: default;
        font: inherit;
        text-align: left;
        text-decoration: none;
        box-sizing: border-box;
      }

      .result-item[href],
      .result-item[data-clickable] {
        cursor: pointer;
      }

      .result-item[href]:hover,
      .result-item[data-clickable]:hover {
        background: ${unsafeCSSVarV2('layer/background/hoverOverlay')};
      }

      .result-item[href]:active,
      .result-item[data-clickable]:active {
        transform: translateY(1px);
      }

      .result-item[href]:focus-visible,
      .result-item[data-clickable]:focus-visible {
        outline: 2px solid ${unsafeCSSVarV2('button/primary')};
        outline-offset: 2px;
      }

      .result-item:first-child {
        margin-top: 0;
      }

      .result-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        line-height: 24px;
        min-height: 36px;
      }

      .result-title {
        ${aiChatMetaTypography}
        font-weight: 400;
        color: ${unsafeCSSVarV2('icon/primary')};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        min-width: 0;
      }

      .result-content {
        ${aiChatMetaTypography}
        color: ${unsafeCSSVarV2('text/secondary')};
        margin-top: 4px;
        display: -webkit-box;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .result-icon,
      .footer-icon {
        width: 18px;
        height: 18px;
        border-radius: 100%;
        background-color: ${unsafeCSSVarV2('layer/background/primary')};

        img {
          width: 18px;
          height: 18px;
          border-radius: 100%;
          border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
        }

        svg {
          width: 18px;
          height: 18px;
          display: block;
          flex-shrink: 0;
          color: ${unsafeCSSVarV2('icon/primary')};
        }
      }

      .result-item[href]:hover .result-title,
      .result-item[href]:hover .result-content,
      .result-item[data-clickable]:hover .result-title,
      .result-item[data-clickable]:hover .result-content {
        color: ${unsafeCSSVarV2('text/primary')};
      }

      .result-action {
        min-width: 44px;
        height: 36px;
        padding: 0 10px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        flex-shrink: 0;
        border-radius: 10px;
        border: 1px solid ${unsafeCSSVarV2('button/primary')};
        background: ${unsafeCSSVarV2('layer/background/secondary')};
        color: ${unsafeCSSVarV2('button/primary')};
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
        transition:
          background-color 0.16s ease,
          border-color 0.16s ease,
          color 0.16s ease,
          box-shadow 0.16s ease;
      }

      .result-action-label {
        ${aiChatControlTypography}
        font-weight: 600;
        white-space: nowrap;
      }

      .result-action-icon {
        width: 16px;
        height: 16px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          width: 16px;
          height: 16px;
          display: block;
          color: currentColor;
        }

        img {
          width: 16px;
          height: 16px;
          border-radius: 4px;
        }
      }

      .result-item[href]:hover .result-action,
      .result-item[data-clickable]:hover .result-action {
        background: ${unsafeCSSVarV2('button/primary')};
        color: ${unsafeCSSVarV2('button/pureWhiteText')};
        box-shadow: 0 3px 10px rgba(0, 0, 0, 0.14);
      }

      .result-icon,
      .footer-icon {
        width: 18px;
        height: 18px;
        border-radius: 100%;
        background-color: ${unsafeCSSVarV2('layer/background/primary')};

        img {
          width: 18px;
          height: 18px;
          border-radius: 100%;
          border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
        }

        svg {
          width: 18px;
          height: 18px;
          display: block;
          flex-shrink: 0;
          color: ${unsafeCSSVarV2('icon/primary')};
        }
      }

      .result-item[href]:hover .result-title,
      .result-item[href]:hover .result-content {
        color: ${unsafeCSSVarV2('text/primary')};
      }

      .result-icon,
      .footer-icon {
        width: 18px;
        height: 18px;
        border-radius: 100%;
        background-color: ${unsafeCSSVarV2('layer/background/primary')};

        img {
          width: 18px;
          height: 18px;
          border-radius: 100%;
          border: 0.5px solid ${unsafeCSSVarV2('layer/insideBorder/border')};
        }

        svg {
          width: 18px;
          height: 18px;
          display: block;
          flex-shrink: 0;
          color: ${unsafeCSSVarV2('icon/primary')};
        }
      }

      .footer-icons {
        display: flex;
        position: relative;
        height: 24px;
        align-items: center;
        opacity: 0.5;
        transition: opacity 0.23s ease;
        user-select: none;
      }

      .footer-icon:not(:first-child) {
        margin-left: -8px;
      }
    }
    .ai-tool-result-wrapper:hover {
      color: ${unsafeCSSVarV2('icon/primary')};

      .footer-icons {
        opacity: 1;
      }
    }

    /* Blue-White theme: dark background for AI chat panel */
    html[data-theme='blue-white'] .ai-tool-result-wrapper {
      background-color: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.15);
      color: rgba(255, 255, 255, 0.7);
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper:hover {
      color: rgba(255, 255, 255, 0.9);
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper .result-title,
    html[data-theme='blue-white'] .ai-tool-result-wrapper .ai-tool-name {
      color: rgba(255, 255, 255, 0.9);
    }

    html[data-theme='blue-white']
      .ai-tool-result-wrapper
      .ai-tool-name.success {
      color: #66bb6a;
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper .ai-icon.success svg {
      color: #66bb6a;
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper .ai-tool-name.error {
      color: #ff8a80;
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper .ai-icon.error svg {
      color: #ff8a80;
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper .result-content {
      color: rgba(255, 255, 255, 0.7);
    }

    html[data-theme='blue-white'] .ai-tool-result-wrapper .result-action {
      border-color: #7eb8f0;
      background: rgba(80, 144, 211, 0.16);
      color: #d8ecff;
      box-shadow: 0 1px 6px rgba(0, 0, 0, 0.18);
    }

    html[data-theme='blue-white']
      .ai-tool-result-wrapper
      .result-item[href]:hover
      .result-action,
    html[data-theme='blue-white']
      .ai-tool-result-wrapper
      .result-item[data-clickable]:hover
      .result-action {
      background: #5090d3;
      border-color: #7eb8f0;
      color: #ffffff;
    }
  `;

  @property({ attribute: false })
  accessor name: string = 'Tool result';

  @property({ attribute: false })
  accessor icon: TemplateResult<1> = ToolIcon();

  @property({ attribute: false })
  accessor footerIcons: TemplateResult<1>[] | string[] = [];

  @property({ attribute: false })
  accessor results: ToolResult[] = [];

  @property({ attribute: false })
  accessor status: 'success' | 'error' | 'running' = 'success';

  @property({ attribute: false })
  accessor width: Signal<number | undefined> | undefined;

  @state()
  private accessor isCollapsed = true;

  private readonly imageProxyURL = DEFAULT_IMAGE_PROXY_ENDPOINT;

  private renderResult(result: ToolResult) {
    const clickable = !!result.href || !!result.onClick;
    const actionLabel =
      result.actionLabel ?? I18n.t('com.streamlain.ai.doc-search.open-source');
    const content = html`
      <div class="result-header">
        <div class="result-title">${result.title}</div>
        ${clickable
          ? html`<span class="result-action">
              <span class="result-action-label">${actionLabel}</span>
              <span class="result-action-icon">
                ${this.renderIcon(result.icon)}
              </span>
            </span>`
          : html`<div class="result-icon">
              ${this.renderIcon(result.icon)}
            </div>`}
      </div>
      ${result.content
        ? html`<div class="result-content">${result.content}</div>`
        : nothing}
    `;

    if (result.href) {
      return html`
        <a
          class="result-item"
          href=${result.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label=${actionLabel}
        >
          ${content}
        </a>
      `;
    }

    if (result.onClick) {
      return html`
        <button
          type="button"
          class="result-item"
          data-clickable="true"
          aria-label=${actionLabel}
          @click=${result.onClick}
        >
          ${content}
        </button>
      `;
    }

    return html`<div class="result-item">${content}</div>`;
  }

  protected override render() {
    const isError = this.status === 'error';
    const isRunning = this.status === 'running';
    return html`
      <div class="ai-tool-result-wrapper">
        <div
          class="ai-tool-header"
          @click=${this.results.length ? this.toggleCard : undefined}
          style="${this.results.length ? 'cursor:pointer' : 'cursor:default'}"
        >
          <div
            class="ai-icon ${isError ? 'error' : isRunning ? '' : 'success'}"
          >
            ${isError ? CloseIcon() : isRunning ? ToolIcon() : DoneIcon()}
          </div>
          <div
            class="ai-tool-name ${isError
              ? 'error'
              : isRunning
                ? ''
                : 'success'}"
          >
            ${this.name}
          </div>
          ${this.results.length
            ? this.isCollapsed
              ? this.renderFooterIcons()
              : html` <div class="ai-icon">${ToggleDownIcon()}</div> `
            : nothing}
        </div>
        <div
          class="ai-tool-results"
          data-collapsed=${this.results.length ? this.isCollapsed : true}
        >
          <div class="ai-tool-result-collapse-wrapper">
            <div class="ai-tool-results-content">
              ${this.results.map(result => this.renderResult(result))}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderFooterIcons() {
    if (!this.footerIcons || this.footerIcons.length === 0) {
      return nothing;
    }

    let maxIcons = 3;
    if (this.width && this.width.value !== undefined) {
      maxIcons = this.width.value <= 400 ? 1 : 3;
    }
    const visibleIcons = this.footerIcons.slice(0, maxIcons);

    return html`
      <div class="footer-icons">
        ${visibleIcons.map(
          (icon, index) => html`
            <div class="footer-icon" style="z-index: ${index}">
              ${this.renderIcon(icon)}
            </div>
          `
        )}
      </div>
    `;
  }

  buildUrl(imageUrl: string) {
    if (imageUrl.startsWith(this.imageProxyURL)) {
      return imageUrl;
    }
    return `${this.imageProxyURL}?url=${encodeURIComponent(imageUrl)}`;
  }

  private renderIcon(icon: string | TemplateResult<1> | undefined) {
    if (!icon) {
      return nothing;
    }

    if (typeof icon === 'string') {
      return html`<div class="image-icon">
        <img
          src=${this.buildUrl(icon)}
          @error=${(e: Event) => {
            const img = e.target as HTMLImageElement;
            img.style.display = 'none';
            const iconElement = img.nextElementSibling as HTMLDivElement;
            iconElement.style.display = 'block';
          }}
        />
        <div style="display: none;">${this.icon}</div>
      </div>`;
    }
    return html`${icon}`;
  }

  private toggleCard() {
    this.isCollapsed = !this.isCollapsed;
  }
}
